import { Navigate } from 'react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { Loader2 } from 'lucide-react';
import { isDevLoginActive } from './DevLoginButton';

/**
 * AdminGuard - Frontend admin permission guard
 *
 * Checks if current user has admin permissions:
 * 1. Dev environment + Dev Login: always allow (backend does dev user check)
 * 2. Dev environment + Auth0 user: allow access
 * 3. Production: backend validates via ADMIN_USER_IDS
 *
 * Note: This is only frontend route protection, real permission check is on backend
 */
interface AdminGuardProps {
  children: React.ReactNode;
}

export function AdminGuard({ children }: AdminGuardProps) {
  const { user, isLoading } = useAuth0();
  const isDev = import.meta.env.DEV;
  const isDevLogin = isDevLoginActive();

  // Dev Login user passes through (backend validates dev token)
  if (isDev && isDevLogin) {
    return <>{children}</>;
  }

  // Auth0 Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-violet-500" />
      </div>
    );
  }

  // Not logged in (neither Auth0 nor Dev Login)
  if (!user) {
    return <Navigate to="/sign-in" replace />;
  }

  // In development, allow all authenticated Auth0 users
  if (isDev) {
    return <>{children}</>;
  }

  // In production, let backend handle admin check
  // Frontend just renders the page, backend will return 403 if not admin
  return <>{children}</>;
}
